import { useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { ArrowRight, BookOpen, Globe, Plug, RefreshCw, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import type { WpConnection } from '@/api/hooks/use-wordpress'
import { useRevokeWpConnection, useWpConnections } from '@/api/hooks/use-wordpress'
import { SyncedArticlesCard } from '@/pages/wordpress-setup/wp-synced-articles-card'

export function WordPressSetupPage(): React.JSX.Element {
  const { data: connectionsData, isLoading: loadingConns } = useWpConnections()
  const connections = connectionsData?.data ?? []
  const connectedSites = connections.filter((c) => c.status === 'connected')
  const hasConnectedSites = connectedSites.length > 0

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <PageHeader />
      {!loadingConns && !hasConnectedSites ? (
        <EmptyState />
      ) : (
        <>
          <ConnectedSitesCard sites={connectedSites} isLoading={loadingConns} />
          {hasConnectedSites && <SyncedArticlesCard />}
        </>
      )}
    </div>
  )
}

function PageHeader(): React.JSX.Element {
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="flex items-center gap-3">
          <Plug className="h-6 w-6 text-brand-500" />
          <h1 className="text-2xl font-semibold text-text-primary">WordPress</h1>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Manage your connected WordPress sites and synced articles.
        </p>
      </div>
      <Link
        to="/wordpress/setup/guide"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-500 hover:text-brand-600"
      >
        <BookOpen className="h-4 w-4" />
        Open Setup Guide
      </Link>
    </div>
  )
}

function EmptyState(): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-surface-3 bg-surface-0 py-16 text-center">
      <Plug className="mb-4 h-12 w-12 text-text-tertiary" />
      <h2 className="text-lg font-semibold text-text-primary">No sites connected yet</h2>
      <p className="mt-2 max-w-sm text-sm text-text-secondary">
        Connect your WordPress site to start syncing articles and collecting subscribers.
      </p>
      <Link
        to="/wordpress/setup/guide"
        className="mt-6 inline-flex items-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600"
      >
        Get Started
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  )
}

function ConnectedSitesCard({
  sites,
  isLoading,
}: {
  sites: WpConnection[]
  isLoading: boolean
}): React.JSX.Element {
  const revokeConnection = useRevokeWpConnection()
  const qc = useQueryClient()
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null)

  function handleDisconnect(id: string): void {
    setRevokeTarget(id)
    revokeConnection.mutate(id, { onSuccess: () => setRevokeTarget(null) })
  }

  function handleSyncArticles(): void {
    qc.invalidateQueries({ queryKey: ['wp-articles'] })
  }

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="flex items-center gap-2">
        <Globe className="h-5 w-5 text-brand-500" />
        <h2 className="text-lg font-semibold text-text-primary">Connected Sites</h2>
      </div>

      <div className="mt-4 space-y-3">
        {isLoading ? (
          <p className="text-sm text-text-tertiary">Loading connections...</p>
        ) : (
          sites.map((site) => (
            <SiteRow
              key={site.id}
              site={site}
              isDisconnecting={revokeTarget === site.id && revokeConnection.isPending}
              onDisconnect={() => handleDisconnect(site.id)}
              onSyncArticles={handleSyncArticles}
            />
          ))
        )}
      </div>

      <div className="mt-4 border-t border-surface-3 pt-4">
        <Link
          to="/wordpress/setup/guide"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-500 hover:text-brand-600"
        >
          + Connect Another Site
        </Link>
      </div>
    </div>
  )
}

interface SiteRowProps {
  site: WpConnection
  isDisconnecting: boolean
  onDisconnect: () => void
  onSyncArticles: () => void
}

function SiteRow({
  site,
  isDisconnecting,
  onDisconnect,
  onSyncArticles,
}: SiteRowProps): React.JSX.Element {
  return (
    <div className="flex items-center justify-between rounded-lg border border-surface-3 p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50">
          <Globe className="h-5 w-5 text-brand-600" />
        </div>
        <div>
          <p className="font-medium text-text-primary">{site.site_name ?? site.name}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
            {site.site_url && (
              <a
                href={site.site_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-brand-500"
              >
                {site.site_url}
              </a>
            )}
            {site.connected_at && (
              <span>
                Connected {formatDistanceToNow(new Date(site.connected_at), { addSuffix: true })}
              </span>
            )}
            {site.last_sync_at && (
              <span className="flex items-center gap-1">
                <RefreshCw className="h-3 w-3" />
                Synced {formatDistanceToNow(new Date(site.last_sync_at), { addSuffix: true })}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSyncArticles}
          className="flex items-center gap-1.5 rounded-md border border-surface-3 px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-1"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Sync Articles
        </button>
        <button
          type="button"
          onClick={onDisconnect}
          disabled={isDisconnecting}
          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-danger hover:bg-red-50 disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
        </button>
      </div>
    </div>
  )
}
